import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCurrencies } from '@/features/catalog/api/currencies';
import type { ProductsListResponse } from '@/features/catalog/types/product.types';
import { useCreateQuotation } from '@/features/sales/api/quotations';
import { apiFetch } from '@/lib/api';
import type { CalculatorProduct } from '../types/calculator.types';

/**
 * Calculator API hooks (Slice 8 PR#1).
 *
 *   - useProductsSearch    — debounced (300ms) product lookup, reusing
 *                            the existing `/products` endpoint. Maps the
 *                            wire Product down to `CalculatorProduct`.
 *   - useCalculatorCurrencies — thin reuse of the catalog `useCurrencies`
 *                            hook (all active currencies).
 *   - useSimulateQuote     — optional BE parity call to
 *                            `POST /api/v1/simulate/quote`.
 *   - useQuotationCreateFromCalculator — reuse of `useCreateQuotation`
 *                            for callers that create directly (the modal
 *                            prefers pre-filling QuotationForm instead).
 */

const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function useProductsSearch(query: string) {
  const debounced = useDebouncedValue(query.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  return useQuery({
    queryKey: ['calculator', 'products', debounced],
    queryFn: async (): Promise<CalculatorProduct[]> => {
      const sp = new URLSearchParams({ page: '1', pageSize: '20', isActive: 'true' });
      if (debounced) sp.set('search', debounced);
      const res = await apiFetch<ProductsListResponse>(`/products?${sp.toString()}`);
      return res.data.map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        defaultSalePrice: p.defaultSalePrice,
        defaultCost: p.defaultCost,
      }));
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCalculatorCurrencies() {
  return useCurrencies({ page: 1, pageSize: 100, sort: 'code', dir: 'asc', isActive: 'active' });
}

// ---------------------------------------------------------------------------
// Simulate — optional BE parity call. The wire body mirrors QuoteInput
// but sends numeric-string decimals; the response is the canonical
// role-gated QuoteOutput shape. Commission was removed in the polish
// PR installment (replaced by closed-table financing); termMonths
// carries the financing term so the BE can mirror the FE installment
// computation.
// ---------------------------------------------------------------------------
export interface SimulateQuoteBody {
  productId: string;
  quantity: string;
  discountPercent: string;
  currencyId: string;
  exchangeRate: string;
  includeTax: boolean;
  /** Financing term in months — lookup key into `FINANCING_TERMS`. */
  termMonths?: number;
}

export interface SimulateQuoteResponse {
  subtotal: string;
  discountAmount: string;
  taxableBase: string;
  taxAmount: string;
  total: string;
  totalInBaseCurrency: string;
  margin?: string | null;
  installment?: string | null;
}

export function useSimulateQuote() {
  return useMutation({
    mutationFn: (body: SimulateQuoteBody) =>
      apiFetch<SimulateQuoteResponse>('/simulate/quote', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useQuotationCreateFromCalculator() {
  return useCreateQuotation();
}
